import {i18n} from '@online-offer/i18n';
import {DataColumn, SectionPrint} from '@online-offer/shared';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {formatNumber, useDateTimeFormatter, useFormatCurrency, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotEmpty, isNotNil} from 'ramda';

import {FinancialProductOffer} from '../../api/types/onlineOfferTypes';
import {useFinancialProducts} from '../../hooks/useFinancialProducts';
import {formatFinancialProductMoney} from './formatFinancialProductMoney';
import {formatFinancialProductPercentage} from './formatFinancialProductPercentage';
import {formatLeasingCostAmount} from './formatLeasingCostAmount';
import {getFinancialProductTypeLabelKey} from './getFinancialProductTypeLabelKey';
import {getFinancialProductUnavailableReasonLabelKey} from './getFinancialProductUnavailableReasonLabelKey';
import {getFinancialProductValidUntilLabel} from './getFinancialProductValidUntilLabel';
import {getLeasingCostSections, LeasingCostItem} from './getLeasingCostSections';
import {getLeasingTargetGroupLabelKey} from './getLeasingTargetGroupLabelKey';

const offerTerms = (offer: FinancialProductOffer, locale: ReturnType<typeof useLocale>) => {
  const selection = offer.selection;

  if (isNil(selection)) {
    return null;
  }

  return match(offer.productType)
    .with('LEASING', () =>
      [
        i18n.t('financialProducts.monthsValue', {value: selection.termMonths}),
        isNil(selection.annualMileage)
          ? null
          : i18n.t('financialProducts.kilometresPerYearValue', {
              value: formatNumber(locale.localeConfig, selection.annualMileage, 0),
            }),
        isNil(selection.leasingTargetGroup)
          ? null
          : i18n.t(getLeasingTargetGroupLabelKey(selection.leasingTargetGroup)),
      ]
        .filter(isNotNil)
        .join(' · ')
    )
    .otherwise(() =>
      [
        i18n.t('financialProducts.monthsValue', {value: selection.termMonths}),
        isNil(selection.downPaymentPercentage)
          ? null
          : `${i18n.t('financialProducts.downPayment')}: ${formatFinancialProductPercentage(
              selection.downPaymentPercentage
            )}`,
        isNil(selection.balloonPaymentPercentage)
          ? null
          : `${i18n.t('financialProducts.balloonPayment')}: ${formatFinancialProductPercentage(
              selection.balloonPaymentPercentage
            )}`,
      ]
        .filter(isNotNil)
        .join(' · ')
    );
};

interface LeasingCostsPrintBlockProps {
  title: string;
  items: {label: string; value: string}[];
}

/** A titled block of label/value rows, left out when it has no rows. */
function LeasingCostsPrintBlock(props: LeasingCostsPrintBlockProps) {
  return (
    <Show when={isNotEmpty(props.items)}>
      <VStack spacing={2}>
        <Text size="small" alternative>
          {props.title}
        </Text>
        {props.items.map((item) => (
          <HStack key={item.label} justify="space-between" spacing={3}>
            <Text size="small" color="secondary">
              {item.label}
            </Text>
            <Text size="small" alternative>
              {item.value}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Show>
  );
}

export function FinancialProductsPrint() {
  const {financialProducts} = useFinancialProducts();
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();
  const locale = useLocale();

  const offers = [
    financialProducts?.standard,
    financialProducts?.balloon,
    financialProducts?.leasing,
  ].filter(isNotNil);
  const offer = isNil(financialProducts?.selectedProduct)
    ? offers.at(0)
    : offers.find((candidate) => candidate.productType === financialProducts.selectedProduct);

  if (isNil(offer)) {
    return null;
  }

  const calculation = offer.calculation;
  const terms = offerTerms(offer, locale);
  const validUntil = getFinancialProductValidUntilLabel(offer.validTo, formatDateTime);
  const leasingCostSections =
    offer.productType === 'LEASING' ? getLeasingCostSections(offer) : null;
  const toPrintRows = (items: LeasingCostItem[]) =>
    items.map((item) => ({
      label: i18n.t(item.labelKey),
      value: formatLeasingCostAmount(item, formatCurrency),
    }));

  return (
    <SectionPrint heading={i18n.t('financialProducts.heading')}>
      <VStack spacing={2}>
        <Show when={isNotNil(calculation)}>
          <HStack spacing={2} align="baseline">
            <Heading size={4} data-testid="financialProductsPrint-monthlyPayment">
              {formatFinancialProductMoney(
                calculation?.leasingTotalMonthlyPayment ?? calculation?.monthlyPayment,
                formatCurrency
              )}
            </Heading>
            <Text size="small" color="secondary">
              {i18n.t('financialProducts.perMonth')}
            </Text>
          </HStack>
        </Show>
        <Show
          when={
            isNotNil(calculation) &&
            isNotNil(calculation.leasingTotalMonthlyPayment) &&
            isNotNil(leasingCostSections?.monthlyServicesTotal)
              ? {calculation, monthlyServicesTotal: leasingCostSections.monthlyServicesTotal}
              : null
          }
        >
          {(breakdown) => (
            <Text size="small" color="secondary" data-testid="financialProductsPrint-breakdown">
              {i18n.t('financialProducts.leasingCosts.breakdown', {
                vehiclePayment: formatFinancialProductMoney(
                  breakdown.calculation.monthlyPayment,
                  formatCurrency
                ),
                services: formatFinancialProductMoney(
                  breakdown.monthlyServicesTotal,
                  formatCurrency
                ),
              })}
            </Text>
          )}
        </Show>
        <Text size="small" color="secondary">
          {[i18n.t(getFinancialProductTypeLabelKey(offer.productType)), terms]
            .filter(isNotNil)
            .join(' · ')}
        </Text>
        <Show when={offer.availability === 'UNAVAILABLE'}>
          <Text size="small" color="danger" data-testid="financialProductsPrint-unavailable">
            {i18n.t(getFinancialProductUnavailableReasonLabelKey(offer.unavailableReason))}
          </Text>
        </Show>
        <Show when={leasingCostSections}>
          {(sections) => (
            <VStack spacing={2} data-testid="financialProductsPrint-leasingCosts">
              <DataColumn
                data={[
                  {
                    label: i18n.t('financialProducts.downPayment'),
                    value: formatFinancialProductMoney(sections.downPayment, formatCurrency),
                  },
                ]}
              />
              <LeasingCostsPrintBlock
                title={i18n.t('financialProducts.leasingCosts.included.title')}
                items={toPrintRows(sections.includes ?? [])}
              />
              <LeasingCostsPrintBlock
                title={i18n.t('financialProducts.leasingCosts.paidOnce.title')}
                items={toPrintRows(sections.paidOnce)}
              />
              <LeasingCostsPrintBlock
                title={i18n.t('financialProducts.leasingCosts.conditional.title')}
                items={toPrintRows(sections.conditional)}
              />
              <Show when={isNotNil(sections.includes) && isNotNil(calculation)}>
                <DataColumn
                  data={[
                    {
                      label: `${i18n.t('financialProducts.totalOver')} ${i18n.t(
                        'financialProducts.monthsValue',
                        {value: calculation?.termMonths}
                      )}`,
                      value: formatFinancialProductMoney(
                        calculation?.leasingTotalForTerm,
                        formatCurrency
                      ),
                    },
                  ]}
                />
                <Show when={sections.downPayment}>
                  {(downPayment) => (
                    <Text size="small" color="tertiary">
                      {`${i18n.t('financialProducts.includingDownPayment')} ${formatFinancialProductMoney(downPayment, formatCurrency)}`}
                    </Text>
                  )}
                </Show>
              </Show>
            </VStack>
          )}
        </Show>
        <DataColumn
          data={[{label: i18n.t('financialProducts.providedBy'), value: offer.providerName}]}
        />
        <Show when={isNotNil(validUntil)}>
          <Text size="small" color="tertiary">
            {validUntil}
          </Text>
        </Show>
        <Show when={financialProducts?.mode === 'CONTRACT_SNAPSHOT'}>
          <Text size="small" color="tertiary">
            {i18n.t('financialProducts.signedSnapshot')}
          </Text>
        </Show>
      </VStack>
    </SectionPrint>
  );
}
